import Box from '@mui/material/Box';
import {
  patchV1CompaniesCompanyIdSettlementsId,
  postV1CompaniesCompanyIdSettlementsSettlementIdExport,
  postV1CompaniesCompanyIdSettlementsSettlementIdPdfs,
  putV1CompaniesCompanyIdSettlementsSettlementIdVoid,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { ClearFiltersLink } from '~components/Filters/ClearFiltersHint';
import { getDateRangePickerIconModeDisplayDate } from '~components/Filters/DateRangePicker';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { SearchParams } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import FileDownloadResultContext from '~contexts/FileDownloadResultContext';
import OutboundEmailProgressContext from '~contexts/OutboundEmailProgressContext';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { OutboundEmailEntry } from '~hooks/useOutboundEmails/sendOutboundEmails';
import useSettlementOutboundEmails from '~hooks/useOutboundEmails/useSettlementOutboundEmails';
import useRutter from '~hooks/useRutter/useRutter';
import useSettlementLineItems from '~hooks/useSettlementLineItems/useSettlementLineItems';
import {
  Settlement,
  SettlementTicketAttachmentOptions,
} from '~hooks/useSettlements/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import {
  actionsColDef,
  createdDateColDef,
  dueDateColDef,
  exportedColDef,
  forColDef,
  jobsColDef,
  orderIdsColDef,
  qtysAndUomsColDef,
  reconcileStateColDef,
  settlementIdColDef,
  SettlementsRow,
  stateColDef,
  totalColDef,
} from '~pages/Approvals/DriverPay/Settlements/settlementsDataGridColumnsDefinition';
import { SETTLEMENT_STATUS_PARAM } from '~pages/Approvals/DriverPayV2/components/Filters/SettlementStatusFilters';
import SettlementsBulkActionsBar from '~pages/Approvals/DriverPayV2/components/SettlementsBulkActionsBar/SettlementsBulkActionsBar';
import { driverPayDateRangeStorageKey } from '~pages/Approvals/DriverPayV2/utils';
import DownloadFilesDialog from '~pages/Approvals/Files/components/DownloadFilesDialog/DownloadFilesDialog';
import DateScopeBanner from '~pages/Dispatch/components/DateScopeBanner';
import connection from '~services/connectionModule';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { SettlementSearchParamName } from '~store/SettlementsStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import { persistDateRange } from '~utils/dateRangePersistence';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { RTU_WAITING_TIMEOUT_IN_MS } from '~utils/rtu/rtu-utils';
import { getFriendlySettlementId } from '~utils/settlements/settlement-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  SettlementForm,
  SettlementFormHandler,
  useSettlementForm,
} from '../components/SettlementForm';
import EmailSettlementsDialog from './EmailSettlementsDialog';
import ExportSettlementConfirmationDialog from './ExportSettlementConfirmationDialog';
import {
  CLOSED_DOWNLOAD_DIALOG_STATE,
  DownloadDialogState,
  lineItemGroupsHaveTimesheetRateType,
} from './helper';
import { SettlementDetails } from './SettlementDetails/SettlementDetails';
import SettlementSheet from './SettlementSheet/SettlementSheet';
import VoidSettlementDrawer from './VoidSettlementDrawer';

// Every settlements filter QS param (QSParams keys minus the preserved identity:
// category, tab, grid, search) plus the status chips param. Deleted together with
// the store clear so the URL→store sync in DriverPaySettlements re-derives the
// same post-clear state instead of reinstating filters.
const FILTER_QS_PARAMS_TO_CLEAR = [
  'start_date',
  'end_date',
  'project_ids',
  'customer_account_ids',
  'vendor_account_ids',
  'truck_ids',
  'driver_ids',
  'dropoff_site_ids',
  'pickup_site_ids',
  'rate_types',
  SETTLEMENT_STATUS_PARAM,
];

const replaceSettlement = (settlements: Settlement[], replacement: Settlement) =>
  settlements.map((settlement) =>
    settlement.id === replacement.id ? replacement : settlement,
  );

const SettlementsDataGrid = observer(() => {
  const { settlementsStore, toasterStore, userStore } = useStores();
  const { getSettlementsByCompanyId, subscribeToSettlementsRTU, updateSettlement } =
    useSettlements();
  const { currencyFormatter } = useCompanyCurrency();
  const settlementsFormRef = useRef<SettlementFormHandler>(null);
  const { dialogHandler } = useSettlementForm();
  const { getAllSettlementLineItems } = useSettlementLineItems();
  const { sendSettlementEmail } = useSettlementOutboundEmails();
  const { enqueueOutboundEmail } = useContext(OutboundEmailProgressContext);
  const { enqueueFileDownload } = useContext(FileDownloadResultContext);
  const chargeBasedSettlements = useFeatureFlag({
    featureFlagKey: FeatureFlags.chargeBasedSettlements,
  });
  const [settlementSheetState, setSettlementSheetState] = useState<{
    isOpen: boolean;
    settlement: Settlement | null;
  }>({
    isOpen: false,
    settlement: null,
  });
  const [selectedSettlementIds, setSelectedSettlementIds] = useState<string[]>([]);
  const [retainedSelectedSettlements, setRetainedSelectedSettlements] = useState<
    Settlement[]
  >([]);
  const [isSubmittingBulkExport, setIsSubmittingBulkExport] = useState(false);
  const [isUpdatingSettlement, setIsUpdatingSettlement] = useState(false);
  const [isGeneratingPdfForSettlementId, setIsGeneratingPdfForSettlementId] =
    useState<string>();
  const [emailDialogState, setEmailDialogState] = useState<{
    isOpen: boolean;
    settlement?: Settlement;
  }>({
    isOpen: false,
  });
  const [voidDialogState, setVoidDialogState] = useState<{
    open: boolean;
    settlement?: Settlement;
    mode?: 'void' | 'void-rebill';
  }>({
    open: false,
  });
  const [downloadDialogState, setDownloadDialogState] = useState<DownloadDialogState>(
    CLOSED_DOWNLOAD_DIALOG_STATE,
  );
  const [exportDialogState, setExportDialogState] = useState<{
    open: boolean;
    settlement?: Settlement;
    isLoading?: boolean;
  }>({
    open: false,
  });
  const companyId = getEffectiveUserCompanyId(userStore);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    goToPage: goToSettlementsPage,
    isFetching: isFetchingSettlements,
    pagination,
    rows: settlements,
    setRows: setSettlements,
    startOver: startSettlementsOver,
  } = useCursorPagination<Settlement>({
    fetchPage: async ({ cursor, limit, link, signal }) => {
      const settlementsPage = await getSettlementsByCompanyId({
        companyId,
        cursor,
        limit,
        link,
        signal,
      });

      if (settlementsPage.shouldExtendToAllDates && !signal.aborted) {
        // Unpersisted, so Invoices keeps its saved range. The store write stops the date
        // filter from refilling the paramless URL with that range.
        settlementsStore.extendSearchToAllDates();
        setSearchParams(
          (params) => {
            params.delete(SearchParams.START_DATE);
            params.delete(SearchParams.END_DATE);
            return params;
          },
          { replace: true },
        );
        // Keeps the grid loading, rather than flashing its empty state, until the
        // refetch for all dates supersedes this request.
        await new Promise<void>((resolve) => {
          signal.addEventListener('abort', () => resolve(), { once: true });
        });
      }

      return settlementsPage;
    },
    limit: MAX_API_PAGE_SIZE,
    onFetchError: (error) =>
      reportRequestError(error, t('error_messages.settlements.failed_to_fetch')),
  });

  const updateSettlementRow = useCallback(
    (updatedSettlement: Settlement) => {
      setSettlements((shownSettlements) =>
        replaceSettlement(shownSettlements, updatedSettlement),
      );
    },
    [setSettlements],
  );

  const upsertSettlementRow = useCallback(
    (receivedSettlement: Settlement) => {
      setSettlements((shownSettlements) => {
        const settlementIsShown = shownSettlements.some(
          ({ id }) => id === receivedSettlement.id,
        );

        if (!settlementIsShown) {
          return [...shownSettlements, receivedSettlement];
        }

        return replaceSettlement(shownSettlements, receivedSettlement);
      });
    },
    [setSettlements],
  );

  const clearSelectedSettlements = useCallback(() => {
    setSelectedSettlementIds([]);
  }, []);

  const openSettlementSheet = useCallback(({ settlement }: SettlementsRow) => {
    setSettlementSheetState({ isOpen: true, settlement });
  }, []);

  const updateSheetSettlement = useCallback(
    (updatedSettlement: Settlement) => {
      setSettlementSheetState((state) => ({
        ...state,
        settlement: updatedSettlement,
      }));
      updateSettlementRow(updatedSettlement);
    },
    [updateSettlementRow],
  );

  const sheetSettlementRow = settlements.find(
    (settlement) => settlement.id === settlementSheetState.settlement?.id,
  );

  useEffect(() => {
    if (sheetSettlementRow) {
      setSettlementSheetState((state) => ({
        ...state,
        settlement: sheetSettlementRow,
      }));
    }
  }, [sheetSettlementRow]);

  const closeSettlementSheet = useCallback(() => {
    setSettlementSheetState((state) => ({ ...state, isOpen: false }));
  }, []);

  const deselectSettlements = useCallback((settlementIds: string[]) => {
    setSelectedSettlementIds((selectedIds) =>
      selectedIds.filter((selectedId) => !settlementIds.includes(selectedId)),
    );
  }, []);

  const isLoading = Boolean(
    isFetchingSettlements || isSubmittingBulkExport || !userStore.userCompany,
  );

  // The selection survives page changes but `settlements` is only the current page, so
  // selected settlements from other pages are kept here for the bulk actions to read.
  useEffect(() => {
    setRetainedSelectedSettlements((previouslyRetained) => {
      const knownSettlements = [...settlements, ...previouslyRetained];

      return selectedSettlementIds.flatMap((selectedId) => {
        const knownSettlement = knownSettlements.find(({ id }) => id === selectedId);

        return knownSettlement ? [knownSettlement] : [];
      });
    });
  }, [selectedSettlementIds, settlements]);

  const settlementsForBulkActions = useMemo(() => {
    const shownSettlementIds = new Set(settlements.map(({ id }) => id));

    return [
      ...settlements,
      ...retainedSelectedSettlements.filter(({ id }) => !shownSettlementIds.has(id)),
    ];
  }, [settlements, retainedSelectedSettlements]);

  const handleClearFiltersKeepSearch = useCallback(() => {
    settlementsStore.clearFiltersExceptSearch();

    // The user chose "All dates": persist explicit nulls so the paramless URL
    // this handler produces below is not re-hydrated from the previous range
    // by the date-filter injection paths.
    persistDateRange(
      driverPayDateRangeStorageKey(searchParams.get(SearchParams.INVOICE_CATEGORY)),
      { endDate: null, startDate: null },
    );

    // URL half — both halves are required: the URL→store sync spreads the
    // current store params first, so leftover URL params would reinstate the
    // cleared filters. The store already holds the post-clear defaults, so the
    // sync re-derives a value-equal object and the grid's fetch effect (keyed
    // on the stringified params) fires exactly once.
    setSearchParams((params) => {
      FILTER_QS_PARAMS_TO_CLEAR.forEach((param) => params.delete(param));
      return params;
    });
  }, [setSearchParams, searchParams]);

  const autoExtendedFromRange = settlementsStore.autoExtendedFromRange;
  const autoExtendedDateLabel = useMemo(
    () =>
      getDateRangePickerIconModeDisplayDate(
        autoExtendedFromRange?.startDate
          ? dayjs(autoExtendedFromRange.startDate).tz()
          : null,
        autoExtendedFromRange?.endDate ? dayjs(autoExtendedFromRange.endDate).tz() : null,
        true,
        true,
      ),
    [autoExtendedFromRange?.startDate, autoExtendedFromRange?.endDate],
  );

  const handleBackToAutoExtendedRange = useCallback(() => {
    settlementsStore.restoreAutoExtendedRange();

    const {
      [SettlementSearchParamName.START_DATE]: startDate,
      [SettlementSearchParamName.END_DATE]: endDate,
    } = settlementsStore.searchParams;

    setSearchParams(
      (params) => {
        if (startDate) {
          params.set(SearchParams.START_DATE, startDate);
        }
        if (endDate) {
          params.set(SearchParams.END_DATE, endDate);
        }
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // until the quiet probe confirms a synchronized connection the export keeps using
  // the manual exported_at path (also while the probe is in flight or when the user
  // cannot read connections - the manual path is the safe fallback, the integration
  // endpoint errors for unlinked companies).
  const { hasSynchronizedConnection } = useRutter();
  const [rutterConnectionSynchronized, setRutterConnectionSynchronized] = useState<
    boolean | null
  >(null);

  // re-probe whenever the export dialog opens so linking or disconnecting Rutter
  // while this grid stays mounted is reflected at the moment of the action
  useEffect(() => {
    hasSynchronizedConnection().then(setRutterConnectionSynchronized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportDialogState.settlement]);

  const isAccountingExportEnabled = rutterConnectionSynchronized === true;

  const verifyAccountingConnection = useCallback(async () => {
    const synchronized = await hasSynchronizedConnection();
    setRutterConnectionSynchronized(synchronized);

    return synchronized === true;
  }, [hasSynchronizedConnection]);

  const exportToPDF = useCallback(
    async (settlement: Settlement) => {
      setIsGeneratingPdfForSettlementId(String(settlement.id));

      try {
        const response = await postV1CompaniesCompanyIdSettlementsSettlementIdPdfs({
          path: {
            'company-id': companyId,
            'settlement-id': String(settlement.id),
          },
        });
        const renderId = response.data.data.ids[0];

        if (!renderId) {
          throw new Error('Settlement PDF render returned no render id');
        }

        enqueueFileDownload(renderId, {
          onSettled: () => setIsGeneratingPdfForSettlementId(undefined),
          timeoutMs: RTU_WAITING_TIMEOUT_IN_MS,
        });
      } catch (error) {
        setIsGeneratingPdfForSettlementId(undefined);
        reportRequestError(error, t('error_messages.settlements.failed_to_generate_pdf'));
      }
    },
    [companyId, enqueueFileDownload],
  );

  const category =
    settlementsStore.searchParams[SettlementSearchParamName.SETTLEMENT_CATEGORY];

  const emailSettlement = useCallback((settlement: Settlement) => {
    setEmailDialogState({
      isOpen: true,
      settlement,
    });
  }, []);

  const handleEmailDialogClose = useCallback(() => {
    setEmailDialogState({
      isOpen: false,
      settlement: undefined,
    });
  }, []);

  const handleEmailSend = useCallback(
    async (
      emails: OutboundEmailEntry[],
      ticketOptions: SettlementTicketAttachmentOptions,
    ) => {
      const { operationId, recipientCount } = await sendSettlementEmail({
        companyId,
        emails,
        ticketOptions,
      });

      enqueueOutboundEmail(operationId, recipientCount);
    },
    [companyId, enqueueOutboundEmail, sendSettlementEmail],
  );

  const handleVoidDialogClose = useCallback(() => {
    setVoidDialogState({
      open: false,
      settlement: undefined,
    });
  }, []);

  const handleVoidSettlement = useCallback(
    async (reason: string): Promise<void> => {
      if (!voidDialogState.settlement) {
        throw new Error('No settlement selected for void');
      }

      try {
        await putV1CompaniesCompanyIdSettlementsSettlementIdVoid({
          path: {
            'settlement-id': String(voidDialogState.settlement.id),
            'company-id': companyId,
          },
          body: {
            void_reason: reason,
          },
        });
        toasterStore.push(
          alert(
            t('approvals.driver_pay.settlements.void_settlement.success', {
              settlementId: getFriendlySettlementId(voidDialogState.settlement, category),
            }),
            AlertTypes.success,
          ),
        );
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_void_settlement'),
        );
      }
    },
    [voidDialogState.settlement, category],
  );

  const voidSettlement = useCallback((settlement: Settlement) => {
    setVoidDialogState({
      open: true,
      settlement,
      mode: 'void',
    });
  }, []);

  const voidRebillSettlement = useCallback((settlement: Settlement) => {
    setVoidDialogState({
      open: true,
      settlement,
      mode: 'void-rebill',
    });
  }, []);

  const handleDownloadFilesClick = useCallback(
    async (settlement: Settlement) => {
      setDownloadDialogState({
        ...CLOSED_DOWNLOAD_DIALOG_STATE,
        isLoading: true,
      });

      try {
        const lineItems = await getAllSettlementLineItems(
          companyId,
          String(settlement.id),
        );

        setDownloadDialogState({
          isOpen: true,
          isLoading: false,
          preselectTimesheet: lineItemGroupsHaveTimesheetRateType(lineItems),
          settlementIds: [String(settlement.id)],
        });
      } catch (error) {
        setDownloadDialogState(CLOSED_DOWNLOAD_DIALOG_STATE);
        connection.handleRequestError(
          error,
          t('approvals.driver_pay.settlements.failed_to_fetch_jobs_for_download'),
        );
      }
    },
    [companyId, getAllSettlementLineItems, toasterStore],
  );

  const handleDownloadDialogClose = useCallback(() => {
    setDownloadDialogState(CLOSED_DOWNLOAD_DIALOG_STATE);
  }, []);

  const exportToAccounting = useCallback((settlement: Settlement) => {
    setExportDialogState({
      open: true,
      settlement,
    });
  }, []);

  const handleExportDialogClose = useCallback(() => {
    setExportDialogState({
      open: false,
      settlement: undefined,
    });
  }, []);

  const handleExportToAccounting = useCallback(async () => {
    if (!exportDialogState.settlement) {
      return;
    }

    const settlement = exportDialogState.settlement;
    const settlementId = String(settlement.id);
    setExportDialogState((prev) => ({ ...prev, isLoading: true }));

    // the dialog-open probe may still be in flight when the user confirms; resolve
    // the routing decision here so a Rutter-only company never races it
    let useAccountingEndpoint = isAccountingExportEnabled;
    if (rutterConnectionSynchronized === null) {
      const synchronized = await hasSynchronizedConnection();
      setRutterConnectionSynchronized(synchronized);
      useAccountingEndpoint = synchronized === true;
    }

    try {
      if (useAccountingEndpoint) {
        await postV1CompaniesCompanyIdSettlementsSettlementIdExport({
          path: {
            'settlement-id': settlementId,
            'company-id': companyId,
          },
        });
      } else {
        await patchV1CompaniesCompanyIdSettlementsId({
          path: { 'company-id': companyId, id: settlementId },
          body: { exported_at: new Date().toISOString() },
        });
      }

      toasterStore.push(
        alert(
          t('approvals.driver_pay.settlements.export_to_accounting.success', {
            settlementId: getFriendlySettlementId(settlement, category),
          }),
          AlertTypes.success,
        ),
      );

      handleExportDialogClose();
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.settlements.failed_to_export'),
      );
      setExportDialogState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [
    exportDialogState.settlement,
    companyId,
    category,
    handleExportDialogClose,
    isAccountingExportEnabled,
    rutterConnectionSynchronized,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const rows = useMemo(() => {
    const category =
      settlementsStore.searchParams[SettlementSearchParamName.SETTLEMENT_CATEGORY];

    return settlements.map((settlement) => {
      const row: SettlementsRow = {
        id: String(settlement.id),
        settlement,
        category,
        isGeneratingPdfForSettlementId,
        utils: {
          currencyFormatter,
          onEditButtonClick(settlement) {
            dialogHandler.open(settlement);
          },
          onExportToPDFButtonClick: exportToPDF,
          onEmailSettlementButtonClick: emailSettlement,
          onVoidSettlementButtonClick: voidSettlement,
          onVoidRebillSettlementButtonClick: voidRebillSettlement,
          onDownloadFilesButtonClick: handleDownloadFilesClick,
          onExportToAccountingButtonClick: exportToAccounting,
          onSettlementChange: updateSettlementRow,
        },
      };

      return row;
    });
  }, [
    settlements,
    settlementsStore.searchParams[SettlementSearchParamName.SETTLEMENT_CATEGORY],
    exportToPDF,
    isGeneratingPdfForSettlementId,
    emailSettlement,
    handleDownloadFilesClick,
    exportToAccounting,
    updateSettlementRow,
  ]);

  const columns = useMemo(() => {
    return [
      createdDateColDef,
      settlementIdColDef,
      forColDef,
      orderIdsColDef,
      jobsColDef,
      qtysAndUomsColDef,
      totalColDef,
      dueDateColDef,
      stateColDef,
      reconcileStateColDef,
      exportedColDef,
      actionsColDef(isAccountingExportEnabled, chargeBasedSettlements),
    ];
  }, [chargeBasedSettlements, isAccountingExportEnabled]);

  const handleSettlementsFormSubmit = useCallback(() => {
    settlementsFormRef.current?.onSubmit?.(async (data) => {
      if (!dialogHandler.settlement?.id) {
        throw new Error('Settlement id not set');
      }

      setIsUpdatingSettlement(true);
      try {
        const updatedSettlement = await updateSettlement(
          companyId,
          dialogHandler.settlement.id,
          data,
        );
        updateSettlementRow(updatedSettlement);

        toasterStore.push(
          alert(
            t('approvals.driver_pay.settlements.settlement_updated', {
              name: getFriendlySettlementId(dialogHandler.settlement, category),
            }),
            AlertTypes.success,
          ),
        );
        dialogHandler.close();
      } catch {
        // Error already handled in the hook.
      } finally {
        setIsUpdatingSettlement(false);
      }
    });
  }, [
    category,
    companyId,
    dialogHandler,
    toasterStore,
    updateSettlement,
    updateSettlementRow,
  ]);

  useEffect(() => {
    dialogHandler.updateBusyState(isUpdatingSettlement);
  }, [isUpdatingSettlement]);

  useEffect(() => {
    if (category) {
      void startSettlementsOver();
    }
  }, [
    category,
    companyId,
    JSON.stringify(settlementsStore.searchParams),
    settlementsStore.sortOption,
    startSettlementsOver,
  ]);

  useEffect(() => {
    return () => {
      settlementsStore.resetDateScope();
    };
  }, []);

  useEffect(() => {
    let subscription: ReturnType<typeof subscribeToSettlementsRTU> | null = null;

    if (companyId) {
      subscription = subscribeToSettlementsRTU(companyId, upsertSettlementRow);
    }

    return () => {
      subscription?.unsubscribe?.();
    };
  }, [companyId, subscribeToSettlementsRTU, upsertSettlementRow]);

  return (
    <>
      <Box>
        {settlementsStore.isDateScopeAutoExtended && (
          <DateScopeBanner
            dateLabel={autoExtendedDateLabel}
            onAction={handleBackToAutoExtendedRange}
            variant="auto-extended"
          />
        )}
        <DataTable
          id="settlements-data-grid"
          columns={columns}
          rows={rows}
          loading={isLoading}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetchingSettlements || isSubmittingBulkExport}
              onNavigate={(link) => void goToSettlementsPage(link)}
              pagination={pagination}
            />
          }
          onRowClick={chargeBasedSettlements ? openSettlementSheet : undefined}
          renderDetailPanel={
            chargeBasedSettlements
              ? undefined
              : (row) => (
                  <SettlementDetails
                    onSettlementChange={updateSettlementRow}
                    settlement={row.settlement}
                  />
                )
          }
          selectedRowIds={selectedSettlementIds}
          onSelectedRowIdsChange={setSelectedSettlementIds}
          noRowsOverlayText={
            settlementsStore.hasUnfilteredResults
              ? t('common.no_results_with_filters')
              : undefined
          }
          noRowsOverlayAction={
            settlementsStore.hasUnfilteredResults ? (
              <ClearFiltersLink onClearFilters={handleClearFiltersKeepSearch} />
            ) : undefined
          }
        />
      </Box>

      <SettlementForm
        ref={settlementsFormRef}
        isBusy={dialogHandler.isBusy}
        isOpen={dialogHandler.isOpen}
        onClose={dialogHandler.close}
        onSubmit={handleSettlementsFormSubmit}
        settlement={dialogHandler.settlement}
      />

      <EmailSettlementsDialog
        companyId={companyId}
        isOpen={emailDialogState.isOpen}
        onClose={handleEmailDialogClose}
        onSend={handleEmailSend}
        settlementIds={
          emailDialogState.settlement?.id ? [emailDialogState.settlement.id] : []
        }
      />

      {chargeBasedSettlements && (
        <SettlementSheet
          category={category}
          isOpen={settlementSheetState.isOpen}
          onClose={closeSettlementSheet}
          onSettlementChange={updateSheetSettlement}
          onVoidClick={voidSettlement}
          settlement={sheetSettlementRow ?? settlementSheetState.settlement}
        />
      )}

      <VoidSettlementDrawer
        open={voidDialogState.open}
        onClose={handleVoidDialogClose}
        onSettlementChange={updateSettlementRow}
        onVoid={handleVoidSettlement}
        settlement={voidDialogState.settlement}
        mode={voidDialogState.mode}
      />

      <DownloadFilesDialog
        isOpen={downloadDialogState.isOpen}
        settlementIds={downloadDialogState.settlementIds}
        onClose={handleDownloadDialogClose}
        preselectTimesheet={downloadDialogState.preselectTimesheet}
      />

      <ExportSettlementConfirmationDialog
        open={exportDialogState.open}
        onClose={handleExportDialogClose}
        onConfirm={handleExportToAccounting}
        settlement={exportDialogState.settlement}
        category={category}
        isLoading={exportDialogState.isLoading}
      />

      <SettlementsBulkActionsBar
        isAccountingExportEnabled={isAccountingExportEnabled}
        onBulkExportSubmittingChange={setIsSubmittingBulkExport}
        onClearSelection={clearSelectedSettlements}
        onDeselectSettlements={deselectSettlements}
        onVerifyAccountingConnection={verifyAccountingConnection}
        selectedSettlementIds={selectedSettlementIds}
        settlements={settlementsForBulkActions}
      />
    </>
  );
});

export default SettlementsDataGrid;
